// tslint:disable: no-empty
import React, { Context, createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import QueryString from 'query-string'
import { DEFAULT_PAGE_SIZE } from '../../constants'
import { callApi, TApiResponse } from '../api'
import { IExperts, TExpert, TExpertsActions, TExpertsFilter } from '../types'
import { RoutePath } from '../../components/routes'
import { isDeepEqual } from '@twilio/conversations/lib/util'

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

const initialExpertsState: IExperts = {
	experts: [],
	expert: null,
	filter: {},
	page: 0,
	count: 0,
	pageSize: DEFAULT_PAGE_SIZE,
	loading: true,
}

const ExpertsContext: Context<{
	experts: IExperts
	actions: TExpertsActions
}> = createContext({
	experts: initialExpertsState,
	actions: {
		getExpertBySlug: (slugId: string) => {},
		resetExpert: () => {},
		getExperts: (filter: TExpertsFilter, newPage?: number, forceRequest?: boolean) => {},
		setFilter: (filter: TExpertsFilter) => {},
		setError: () => {},
		setPage: (page: number) => {},
		setPageSize: (pageSize: number) => {},
	},
})

export const ExpertsProvider = (props: PropsWithChildren<{}>) => {
	const [loading, setLoading] = useState<boolean>(true)
	const [error, setProviderError] = useState<string | null>(null)
	const [expert, setExpert] = useState<TExpert>()
	const [experts, setExperts] = useState<TExpert[]>([])
	const [page, setPage] = useState<number>(0)
	const [count, setCount] = useState<number>(0)
	const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
	const [filter, setProviderFilter] = useState<TExpertsFilter>()

	const history = useHistory()

	const setFilter = useCallback(
		(newFilter: TExpertsFilter) => {
			setProviderFilter(newFilter)
			history.replace({
				pathname: RoutePath.Experts,
				search: QueryString.stringify(newFilter, { arrayFormat: 'comma', skipNull: true }),
			})
		},
		[history]
	)

	useEffect(() => {
		if (!!filter) fetchExperts()
	}, [page, pageSize])

	const fetchExperts = async (newFilter?: TExpertsFilter, newPage?: number, forceRequest?: boolean) => {
		if (!forceRequest && isDeepEqual(newFilter, filter)) return

		setLoading(true)

		if (newFilter) {
			setFilter(newFilter)
		}

		const expertsResponse: TApiResponse = await callApi('experts', {
			method: 'POST',
			query: {
				page: newPage ?? page,
				pageSize,
			},
			body: { ...(newFilter || filter) },
		})

		const { data } = expertsResponse.payload

		if (expertsResponse.ok) {
			setProviderError(null)
			setExperts(data.experts)
			setCount(data.count)
			setPage(parseInt(data.page, 10))
			setPageSize(parseInt(data.pageSize, 10))
			setLoading(false)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get Experts!')
		}
	}

	const fetchExpertBySlug = async (slugId: string) => {
		setLoading(true)
		const expertResponse: TApiResponse = await callApi(`experts/slug/${slugId}`, {})

		const { data } = expertResponse.payload

		if (expertResponse.ok) {
			setLoading(false)
			setProviderError(null)
			setExpert(data.expert)
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get Expert!')
		}
	}

	const getExperts = useCallback(fetchExperts, [filter, page, pageSize, setFilter])
	const getExpertBySlug = useCallback(fetchExpertBySlug, [])

	const setError = useCallback(
		(errorMessage?: string) => {
			setProviderError(errorMessage || null)
		},
		[setProviderError]
	)

	const resetExpert = useCallback(() => {
		setLoading(false)
		setProviderError(null)
		setExpert(null)
	}, [])

	const actions: TExpertsActions = {
		setFilter,
		getExperts,
		getExpertBySlug,
		resetExpert,
		setError,
		setPage,
		setPageSize,
	}

	return (
		<ExpertsContext.Provider
			value={{
				experts: { filter, experts, expert, page, count, pageSize, loading, error },
				actions,
			}}
		>
			{props.children}
		</ExpertsContext.Provider>
	)
}

export const useExpertsContext: () => {
	experts: IExperts
	actions: TExpertsActions
} = () => {
	return useContext(ExpertsContext)
}
